import React, { useEffect, useRef } from 'react';
import PageWrapper from 'app/components/materials/PageWrapper';
import WaitingOwl from 'app/components/svgs/mascot/DoorWaiting';
import { useI18n } from 'app/components/localization';
import OwlMessagePage from 'app/components/pages/OwlMessagePage';
import GetServerTime from 'app/containers/rtc/BookingContext/GetServerTime';

interface IProps {
  loadLesson: () => void;
  validFrom?: Date;
}

interface IDeps {
  shouldReload: boolean;
}

const LESSON_POLL_TIME = 1000 * 10;

const LessonExamPendingPage: React.FunctionComponent<IProps & IDeps> = React.memo(({ shouldReload, loadLesson }) => {
  const { t } = useI18n();
  const attemptedFetch = useRef<boolean>(false);

  useEffect(() => {
    if (!attemptedFetch.current && shouldReload) {
      attemptedFetch.current = true;
      // Calling loadLesson will cause this component to be unmounted by the parent AutoLoader, so use timeout
      // instead of interval. In most cases loadLesson should only need to be called once after the exam is viewable.
      const loadTimeoutId = setTimeout(loadLesson, LESSON_POLL_TIME);
      return () => {
        clearTimeout(loadTimeoutId);
      };
    }
  }, [shouldReload, attemptedFetch, loadLesson]);

  return (
    <PageWrapper title={t('http-status-page.tieup.access-pending.header')}>
      <OwlMessagePage
        image={<WaitingOwl width="100%" />}
        header={t('http-status-page.tieup.access-pending.header')}
        message={t('http-status-page.tieup.access-pending.message')}
        buttons={null}
      />
    </PageWrapper>
  );
});


const LessonExamPendingPageInjectDeps: React.FunctionComponent<IProps> = ({ loadLesson, validFrom }: IProps) => (
  <GetServerTime>{(servertime) => {
    // Add the lesson poll time to account for delay when attempting to reload the lesson
    const shouldReload = validFrom && servertime.valueOf() + LESSON_POLL_TIME > validFrom.valueOf() || false;
    return <LessonExamPendingPage shouldReload={shouldReload} loadLesson={loadLesson} />;
  }}</GetServerTime>
);

export default LessonExamPendingPageInjectDeps;
